import React, {FC, useState} from 'react'
import {
  Box,
  Card,
  Typography,
  Divider,
  TextField,
  Tooltip,
  IconButton,
  Paper,
} from '@mui/material'
import { autonomyColor } from '../../../global-styles/autonomyColor'
import { DataGrid, GridRowParams } from '@mui/x-data-grid'
import { useCustomerContext } from '../CustomerContext'
import { DrawerComponent } from 'shared/DrawerComponent'
import { FormDrawerComponent } from 'shared/FormDrawerComponent'
import { InsurancePolicyForm } from './InsurancePolicyForm'
import { InvoiceDetails } from './InvoiceDetails'
import { useCustomerInvoices } from '../useListCustomerInvoices'
import { useInvoiceDetails } from '../useInvoiceDetails'
import { StatusPill } from 'shared/StatusPill'
import { getGridRowClass } from '../../../utils/getGridRowClass'
import { formatMoney } from '../../../utils/currency-formatter'
import { Delete, Edit, CheckBox } from '@mui/icons-material'
import { useCustomerBilling } from '../useCustomerBilling'
import { useTheme } from '../../../utils/themeContext'
import { getColorsForTheme } from '../../../global-styles/clientColors'
import toast from 'react-hot-toast'
import { useInsurance } from '../Insurance/useInsurance'
import {sleep} from '../../../utils/utils'

export const CustomerInvoices = () => {
  const { customerId, subscriptionId } = useCustomerContext()
  return (
    <Payments2Invoices
      key={`${customerId}:${subscriptionId}`}
      subscriptionId={subscriptionId}
      customerId={customerId}
    />
  )
}

const Payments2Invoices: FC<{
  customerId: string
  subscriptionId: string
}> = ({ customerId, subscriptionId }) => {
  const { formattedInvoices, refetchInvoices } = useCustomerInvoices(customerId)
  const { setInvoiceId, isInvoiceDetailsDrawerOpen } = useInvoiceDetails()
  const {
    useRecurringBillableItems,
    doUpdateSubscriptionAmount,
    isSavingSubscription,
    deleteMileagePackage,
  } = useCustomerBilling(customerId, subscriptionId)
  const {cancelInsurance, upsertInsurancePolicies} = useInsurance(customerId, subscriptionId)
  const { recurringBillableItems, refetchBillableItems } =
    useRecurringBillableItems()
  const subscriptionItem = recurringBillableItems?.find(
    (rbi) => rbi.productType === 1
  )
  const [isEditingSubscription, setIsEditingSubscription] = useState(false)
  const [subscriptionAmount, setSubscriptionAmount] = useState('')
  const [isRefreshingSubscription, setIsRefreshingSubscription] = useState(false)
  const isSubscriptionSavePending =
    isSavingSubscription || isRefreshingSubscription
  const isSubscriptionAmountValid =
    /^\d+$/.test(subscriptionAmount) &&
    Number.isSafeInteger(Number(subscriptionAmount))
  const { doCancelInsurance } = cancelInsurance()
  const [policyNumber, setPolicyNumber] = useState()
  const [providerName, setProviderName] = useState()
  const [effectiveDate, setEffectiveDate] = useState(0)
  const [expirationDate, setExpirationDate] = useState(0)
  const { doUpsertInsurancePolicies } = upsertInsurancePolicies(policyNumber,providerName,effectiveDate,expirationDate)
  const { doDeleteMileagePackage } = deleteMileagePackage(
    recurringBillableItems
  )
  const { currentTheme } = useTheme()
  const themeColors = getColorsForTheme(currentTheme)
  const style = { display: 'flex', alignItems: 'center', padding: '1rem' }
  const [isInsuranceDrawerOpen, setIsInsuranceDrawerOpen] = React.useState(false)
  const [hideCancelInsuranceButton, setHideCancelInsuranceButton] =
    React.useState(false)
  const [hideDeleteMileageButton, setHideDeleteMileageButton] =
    React.useState(false)

  const handleInsuranceCancelClick = () => {
    setIsInsuranceDrawerOpen(true)
  }

  const handleInsuranceFormSubmit = async (data) => {
    try {
      setPolicyNumber(data.policyNumber)
      setProviderName(data.providerName)
      setEffectiveDate(data.effectiveDate)
      setExpirationDate(data.expirationDate)
      await sleep(1000)
      doUpsertInsurancePolicies()
        .then(() =>
      doCancelInsurance()
        .then(() => setHideCancelInsuranceButton(true))
        .then(() => refetchBillableItems())
        .then(() => setIsInsuranceDrawerOpen(false))
        )

      toast.success('Insurance policy cancelled successfully')
    } catch (error) {
      console.error('Error cancelling insurance:', error)
      toast.error('Error cancelling insurance policy')
    }
  }

  async function onClickSaveAmount(e: React.FormEvent) {
    e.preventDefault()
    if (!subscriptionItem || isSubscriptionSavePending) {
      return
    }
    if (!isSubscriptionAmountValid) {
      toast.error('Enter a valid subscription amount in whole cents')
      return
    }
    try {
      setIsRefreshingSubscription(true)
      await doUpdateSubscriptionAmount({
        recurringBillableItem: subscriptionItem,
        amount: Number(subscriptionAmount),
      })
      await refetchBillableItems({ throwOnError: true })
      setIsEditingSubscription(false)
    } catch (error) {
      console.error('Error saving subscription amount:', error)
      toast.error('Error saving subscription amount. Please try again.')
    } finally {
      setIsRefreshingSubscription(false)
    }
  }
  function onClickDeleteMileage(recurringBillableItems) {
    doDeleteMileagePackage(recurringBillableItems)
      .then(() => setHideDeleteMileageButton(true))
      .then(() => refetchBillableItems())
  }

  return (
    <Card>
      <Box sx={{ ml: 1, margin: '20px' }}>
        <Typography color="inherit" variant="subtitle2">
          Subscription ID:{' '}
        </Typography>
        <Typography
          sx={{ fontWeight: 'bold' }}
          color={autonomyColor.white}
          variant="subtitle2"
        >
          {subscriptionId}
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ ml: 1, margin: '20px' }}>
        <Typography color="inherit" variant="h5">
          Recurring Costs
        </Typography>
        {!recurringBillableItems && (
          <Typography style={style} color="inherit" variant="subtitle2">
            Loading...
          </Typography>
        )}
        {subscriptionItem && (
            <Paper
              component="form"
              onSubmit={onClickSaveAmount}
              sx={{ display: 'flex', alignItems: 'center', padding: '1rem' }}
            >
              <TextField
                id={'subscriptionAmount'}
                label={'Subscription:'}
                color={isEditingSubscription ? 'error' : 'primary'}
                autoFocus={isEditingSubscription}
                variant={isEditingSubscription ? 'outlined' : 'standard'}
                value={
                  isEditingSubscription
                    ? subscriptionAmount
                    : formatMoney(subscriptionItem.amount)
                }
                onChange={(e) =>
                  setSubscriptionAmount(e.target.value)
                }
                helperText={isEditingSubscription ? 'Amount in cents' : undefined}
                error={isEditingSubscription && !isSubscriptionAmountValid}
                disabled={!isEditingSubscription || isSubscriptionSavePending}
              />
              {!isEditingSubscription && (
                <Tooltip title={'Edit Subscription'}>
                  <IconButton
                    color="primary"
                    sx={{
                      p: '10px',
                      color: themeColors.buttonColor,
                      '&:hover': {
                        color: themeColors.buttonHover,
                      },
                    }}
                    aria-label="edit subscription"
                    onClick={() => {
                      setSubscriptionAmount(String(subscriptionItem.amount))
                      setIsEditingSubscription(true)
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              )}
              {isEditingSubscription && (
                <Tooltip title={'Save Subscription Changes'}>
                  <IconButton
                    color="primary"
                    sx={{
                      p: '10px',
                      color: themeColors.buttonColor,
                      '&:hover': {
                        color: themeColors.buttonHover,
                      },
                    }}
                    aria-label="save subscription changes"
                    type="submit"
                    disabled={isSubscriptionSavePending || !isSubscriptionAmountValid}
                  >
                    <CheckBox />
                  </IconButton>
                </Tooltip>
              )}
            </Paper>
          )}
        {recurringBillableItems &&
          recurringBillableItems.find((rbi) => rbi.productType === 4) && (
            <Paper
              component="form"
              sx={{ display: 'flex', alignItems: 'center', padding: '1rem' }}
            >
              <TextField
                label={'Insurance:'}
                variant={'standard'}
                defaultValue={formatMoney(
                  recurringBillableItems.find((rbi) => rbi.productType === 4)
                    .amount
                )}
                disabled={true}
              />
              {!hideCancelInsuranceButton && (
                <Tooltip title={'Cancel Insurance'}>
                  <IconButton
                    color="primary"
                    sx={{
                      p: '10px',
                      color: themeColors.buttonColor,
                      '&:hover': {
                        color: themeColors.buttonHover,
                      },
                    }}
                    aria-label="cancel insurance"
                    onClick={handleInsuranceCancelClick}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              )}
            </Paper>
          )}
        {recurringBillableItems &&
          recurringBillableItems.find((rbi) => rbi.productType === 23) && (
            <Paper
              component="form"
              sx={{ display: 'flex', alignItems: 'center', padding: '1rem' }}
            >
              <TextField
                label={'Mileage Package:'}
                variant={'standard'}
                defaultValue={formatMoney(
                  recurringBillableItems.find((rbi) => rbi.productType === 23)
                    .amount
                )}
                disabled={true}
              />
              {!hideDeleteMileageButton && (
                <Tooltip title={'Delete Mileage Package'}>
                  <IconButton
                    color="primary"
                    sx={{
                      p: '10px',
                      color: themeColors.buttonColor,
                      '&:hover': {
                        color: themeColors.buttonHover,
                      },
                    }}
                    aria-label="delete mileage package"
                    onClick={() => onClickDeleteMileage(recurringBillableItems)}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              )}
            </Paper>
          )}
      </Box>
      <Divider />
      <DataGrid
        autoHeight
        rowHeight={35}
        isRowSelectable={() => true}
        getRowClassName={(params) =>
          getGridRowClass(params.indexRelativeToCurrentPage)
        }
        columns={invoiceColumns}
        rows={formattedInvoices}
        onRowClick={(params: GridRowParams) => {
          setInvoiceId(params.row.id)
        }}
        getRowId={(row) => row.documentNumber}
        paginationModel={{page: 0, pageSize: 20}}
        pagination={true}
        pageSizeOptions={[10]}
      />
      {isInvoiceDetailsDrawerOpen && (
        <DrawerComponent
          isDrawerOpen={true}
          setIsDrawerOpen={() => {
            setInvoiceId()
          }}
          header={'Invoice Details'}
          childComponent={() => {
            return (
              <InvoiceDetails
                invoices={formattedInvoices}
                refetchInvoices={refetchInvoices}
                subscriptionId={subscriptionId}
              />
            )
          }}
        />
      )}
      {isInsuranceDrawerOpen && (
        <FormDrawerComponent
          isDrawerOpen={isInsuranceDrawerOpen}
          setIsDrawerOpen={setIsInsuranceDrawerOpen}
          header="Cancel Insurance Policy"
          handleSave={handleInsuranceFormSubmit}
          form={InsurancePolicyForm}
          defaultValues={{
            policyNumber: '',
            providerName: '',
            effectiveDate: null,
            expirationDate: null,
          }}
        />
      )}
    </Card>
  )
}

const invoiceColumns = [
  // {
  //   field: 'type',
  //   headerName: 'Invoice Type',
  //   width: 150,
  // },
  {
    field: 'date',
    headerName: 'Date',
    width: 150,
  },
  {
    field: 'dueDate',
    headerName: 'Due Date',
    width: 150,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 150,
    renderCell: (props) => (
      <StatusPill color={'issued'}>{props.row.status}</StatusPill>
    ),
  },
  {
    field: 'amount',
    headerName: 'Amount',
    width: 100,
    cellClassName: 'rtl',
  },
  {
    field: 'balance',
    headerName: 'Balance',
    width: 100,
    cellClassName: 'rtl',
  },
  {
    field: 'documentNumber',
    headerName: 'Document Number',
    width: 150,
  },
  {
    field: 'id',
    headerName: 'Invoice ID',
    width: 350,
  },
]
